import { type RefObject, useCallback, useLayoutEffect, useRef } from 'react';

import { useKeyboardHighlightProvider } from '@atlassian/search-dialog/search-keyboard';

import { MATCHING_WORK_ITEM_ID } from '../../../../common/constants/intent-router';
import { normalizeWorkItemKey } from '../../../../common/ui/quick-find/normalize-work-item-key';
import { IntentMode } from '../../../../common/utils/intent-detection';

const INTENT_ROW_TRACKING_ID_BY_MODE: Record<IntentMode | typeof MATCHING_WORK_ITEM_ID, string> = {
	[MATCHING_WORK_ITEM_ID]: `intentRouterWorkItem:${MATCHING_WORK_ITEM_ID}`,
	[IntentMode.FPS]: 'intentRouter:intent-all-apps',
	[IntentMode.GNIN]: 'intentRouter:intent-jira-work-items',
};

/**
 * Keeps the keyboard-highlighted row in sync with the currently selected intent mode.
 *
 * Uses the shared KeyboardHighlightProvider state so selection behaves like normal
 * keyboard navigation (Enter triggers the selected row, etc). Users can still change
 * selection via ArrowUp/ArrowDown; we re-apply the primaryMode row when:
 *  - `primaryMode` changes (intent detection picked a new mode), or
 *  - the user types (`query` changes) and the primaryMode row is not currently highlighted
 *    (e.g. because the user navigated away with arrow keys).
 *
 * Guard logic runs synchronously in useLayoutEffect (before paint) to prevent
 * race conditions with arrow key navigation. The actual highlight application
 * is deferred to a requestAnimationFrame for smoother visual transitions.
 */
export const useIntentRouterKeyboardPreselection = ({
	primaryMode,
	query,
	isLoadingWorkItem,
	hasMatchingWorkItem = false,
	showMatchingWorkItem = false,
	inputRef,
}: {
	primaryMode: IntentMode;
	query: string;
	isLoadingWorkItem?: boolean;
	hasMatchingWorkItem?: boolean;
	showMatchingWorkItem?: boolean;
	inputRef?: RefObject<HTMLInputElement>;
}): void => {
	const { highlightComponentAt, unhighlightAll, keyboardNavComponents, getSelectedIndex } =
		useKeyboardHighlightProvider() ?? {};

	const setActiveDescendant = useCallback(
		(trackingId: string | null) => {
			const input = inputRef?.current;
			if (!input) {
				return;
			}
			if (trackingId) {
				input.setAttribute('aria-activedescendant', trackingId);
			} else {
				input.removeAttribute('aria-activedescendant');
			}
		},
		[inputRef],
	);

	const lastAppliedModeRef = useRef<IntentMode | null>(null);
	const prevQueryRef = useRef(query);
	const highlightRafIdRef = useRef<number | null>(null);

	useLayoutEffect(() => {
		const currentIdx = getSelectedIndex?.() ?? -1;
		const isWorkItemSkeletonDisplayed = Boolean(
			showMatchingWorkItem && isLoadingWorkItem && normalizeWorkItemKey(query),
		);

		if (!hasMatchingWorkItem && isWorkItemSkeletonDisplayed) {
			// loading skeleton is displayed, don't highlight any intent detection option
			if (currentIdx > -1) {
				unhighlightAll?.();
				setActiveDescendant(null);
			}
			return;
		}

		const desiredTrackingId =
			INTENT_ROW_TRACKING_ID_BY_MODE[hasMatchingWorkItem ? MATCHING_WORK_ITEM_ID : primaryMode];

		// We can only apply selection once the intent row is registered.
		const desiredIdx =
			(hasMatchingWorkItem
				? keyboardNavComponents
				: // exclude the matching work item as it might take some time to unregister
					keyboardNavComponents?.filter(
						(c) => c.navigationTrackingId !== INTENT_ROW_TRACKING_ID_BY_MODE[MATCHING_WORK_ITEM_ID],
					)
			)?.findIndex((c) => c.navigationTrackingId === desiredTrackingId) ?? -1;

		if (desiredIdx < 0) {
			return;
		}

		// Mode already applied — decide whether to re-apply.
		if (!showMatchingWorkItem && lastAppliedModeRef.current === primaryMode) {
			const queryChanged = prevQueryRef.current !== query;
			prevQueryRef.current = query;

			// Row is still correctly selected — nothing to do.
			if (currentIdx === desiredIdx) {
				return;
			}

			// A different row is selected (user navigated with arrow keys).
			// Only re-apply if the user typed (query changed); ignore
			// keyboardNavComponents-only changes (e.g. FlipList animation).
			if (currentIdx !== undefined && !queryChanged) {
				return;
			}
		}

		// Mark as applied synchronously to prevent duplicate scheduling
		// on rapid re-renders before the RAF fires.
		lastAppliedModeRef.current = primaryMode;

		// Cancel any previously scheduled RAF.
		if (highlightRafIdRef.current !== null) {
			cancelAnimationFrame(highlightRafIdRef.current);
		}

		// Defer the visual highlight to the next animation frame for a smoother transition.
		highlightRafIdRef.current = requestAnimationFrame(() => {
			highlightRafIdRef.current = null;
			unhighlightAll?.();
			highlightComponentAt?.(desiredIdx);
			keyboardNavComponents?.[desiredIdx]?.onHighlightedCallback?.(desiredIdx);
			setActiveDescendant(desiredTrackingId);
		});

		return () => {
			if (highlightRafIdRef.current !== null) {
				cancelAnimationFrame(highlightRafIdRef.current);
				highlightRafIdRef.current = null;
			}
		};
	}, [
		keyboardNavComponents,
		keyboardNavComponents?.length,
		hasMatchingWorkItem,
		isLoadingWorkItem,
		showMatchingWorkItem,
		primaryMode,
		query,
		getSelectedIndex,
		highlightComponentAt,
		unhighlightAll,
		setActiveDescendant,
	]);
};
